import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSection8, S8_STATUSES, Section8Case } from "@/hooks/use-section8";
import { usePipeline } from "@/hooks/use-pipeline";
import { Pencil, Trash2, ClipboardCheck } from "lucide-react";
import { MockProperty } from "@/data/mockProperties";
import { CaseWizardDialog } from "@/components/section8/CaseWizardDialog";
import { DomainTermInfo } from "@/components/ui/domain-term-info";

export function CaseTracker() {
  const { cases, deleteCase } = useSection8();
  const { savedProperties } = usePipeline();
  const [editCase, setEditCase] = useState<Section8Case | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Section8Case | null>(null);

  const getPropertyLabel = (spId: string) => {
    const sp = savedProperties.find((s) => s.id === spId);
    if (!sp) return "Unknown";
    const pd = sp.property_data as unknown as MockProperty;
    return pd?.address || "Property";
  };

  const getStatusInfo = (status: string) =>
    S8_STATUSES.find((s) => s.value === status) || S8_STATUSES[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Section 8 Cases</h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">
            <span>Track</span>
            <span className="inline-flex items-center gap-0.5">
              PHA
              <DomainTermInfo termId="PHA" size="md" />
            </span>
            <span>applications and approval progress</span>
          </p>
        </div>
        <CaseWizardDialog />
      </div>

      {/* Edit reuses the create wizard, pre-filled and saving via update. */}
      <CaseWizardDialog
        editCase={editCase}
        open={!!editCase}
        onOpenChange={(o) => { if (!o) setEditCase(null); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Section 8 case?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the case for{" "}
              <span className="font-medium text-foreground">
                {deleteTarget ? getPropertyLabel(deleteTarget.pipeline_property_id) : ""}
              </span>
              , including any saved progress. This can't be undone. The property will become
              available for a new case afterward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCase.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCase.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!deleteTarget) return;
                deleteCase.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {deleteCase.isPending ? "Deleting…" : "Delete case"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {cases.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No Section 8 cases yet. Create one to start tracking.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-soft">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="inline-flex items-center gap-0.5">
                    PHA
                    <DomainTermInfo termId="PHA" />
                  </span>
                </TableHead>
                <TableHead>Asking Rent</TableHead>
                <TableHead>Voucher Amt</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cases.map((c) => {
                const status = getStatusInfo(c.status);
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium text-sm">{getPropertyLabel(c.pipeline_property_id)}</TableCell>
                    <TableCell><Badge variant="outline" className={status.color}>{status.label}</Badge></TableCell>
                    <TableCell className="text-sm">{c.pha_name || "—"}</TableCell>
                    <TableCell className="text-sm">{c.asking_rent ? `$${Number(c.asking_rent).toLocaleString()}` : "—"}</TableCell>
                    <TableCell className="text-sm">{c.tenant_voucher_amount ? `$${Number(c.tenant_voucher_amount).toLocaleString()}` : "—"}</TableCell>
                    <TableCell className="text-sm">{c.tenant_name || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Edit case" onClick={() => setEditCase(c)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label="Delete case" onClick={() => setDeleteTarget(c)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
